import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Check, ChevronDown, Search } from "lucide-react";
import { forwardRef, useCallback, useEffect, useId, useMemo, useRef, useState, } from "react";
import { controlClasses, mergedControlTriggerClasses } from "./input";
import { Popover, PopoverContent, PopoverTrigger, scrollableInDialog, } from "./popover";
import { DEFAULT_SEARCH_THRESHOLD, firstEnabledIndex, indentFor, matches, nextEnabledIndex, normalize, } from "./searchable-select-shared";
import { cn } from "../lib/cn";
import { fieldWidthClass } from "../lib/field-width";
export { DEFAULT_SEARCH_THRESHOLD };
export const SearchableSelect = forwardRef(function SearchableSelect({ options, value, onValueChange, label, placeholder, searchLabel, searchPlaceholder, emptyMessage, searchThreshold = DEFAULT_SEARCH_THRESHOLD, disabled, width, className, contentClassName, triggerVariant = "control", onOpen, 'aria-label': ariaLabel, 'aria-labelledby': ariaLabelledBy, ...triggerProps }, ref) {
    const reactId = useId();
    const listboxId = `${reactId}-listbox`;
    const optionId = (index) => `${reactId}-option-${index}`;
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState("");
    const [activeIndex, setActiveIndex] = useState(-1);
    const searchRef = useRef(null);
    const listRef = useRef(null);
    const activeRef = useRef(null);
    /**
     * True from the moment the list opens until the active row has been CENTRED
     * in it once. See `centerActiveRow` / `attachList` for why that cannot simply
     * happen in the open effect.
     */
    const centerPendingRef = useRef(false);
    const showSearch = options.length > searchThreshold;
    const selected = useMemo(() => options.find((option) => option.value === value) ?? null, [options, value]);
    const filtered = useMemo(() => {
        const needle = normalize(query.trim());
        if (needle === "")
            return options;
        return options.filter((option) => matches(option, needle));
    }, [options, query]);
    /**
     * Keep the active row inside the list. Filtering shrinks `filtered` under a
     * stale `activeIndex`, and an out-of-range `aria-activedescendant` points at
     * an element that no longer exists — the screen reader then announces
     * nothing at all while the user types.
     */
    useEffect(() => {
        setActiveIndex((current) => {
            if (current >= 0 && current < filtered.length && !filtered[current]?.disabled) {
                return current;
            }
            return firstEnabledIndex(filtered);
        });
    }, [filtered]);
    /**
     * Opening centres the SELECTED row (TimeField showing 09:00 opens on 09:00,
     * not on midnight); arrowing afterwards scrolls by `nearest`, one row at a
     * time, rather than yanking every step to the middle of the panel.
     *
     * Centring cannot happen in an open effect, which is what this used to try
     * (`scrollIntoView` there). When the popup first mounts, Radix has not yet
     * positioned it: the list's `max-h` reads
     * `--radix-popover-content-available-height`, which floating-ui only sets
     * after its first (async) measurement, and until then the `min()` is invalid
     * and the list has NO max-height — all 96 rows laid out at full height, no
     * overflow, nothing to scroll. The scroll was a no-op, and the list then
     * shrank to its real height with `scrollTop` still 0. So the list is watched
     * with a `ResizeObserver` (attached in `attachList`, the list's callback
     * ref) and centred on the first observation where it actually overflows.
     *
     * Both paths set `scrollTop` on the LIST directly rather than calling
     * `scrollIntoView`, which also scrolls every scrollable ancestor — the page,
     * or a Dialog's body — to bring the row into the viewport.
     */
    const centerActiveRow = useCallback(() => {
        const list = listRef.current;
        const row = activeRef.current;
        if (!centerPendingRef.current || !list || !row)
            return;
        // Not yet constrained (see above): nothing to scroll, try again on resize.
        if (list.clientHeight === 0 || list.scrollHeight <= list.clientHeight)
            return;
        centerPendingRef.current = false;
        list.scrollTop = Math.max(0, row.offsetTop - (list.clientHeight - row.offsetHeight) / 2);
    }, []);
    /**
     * The list's callback ref. A callback ref, not an effect keyed on `open`:
     * Radix's Portal mounts its children one render AFTER `open` flips, so an
     * effect would find `listRef.current` still null. React 19 runs the returned
     * function when the node detaches, which is where the observer goes away.
     * Refs attach child-first, so `activeRef` is already set by the time this runs.
     */
    const attachList = useCallback((node) => {
        listRef.current = node;
        if (!node)
            return undefined;
        centerActiveRow();
        if (typeof ResizeObserver === "undefined")
            return undefined;
        const observer = new ResizeObserver(() => centerActiveRow());
        observer.observe(node);
        return () => {
            observer.disconnect();
            listRef.current = null;
        };
    }, [centerActiveRow]);
    useEffect(() => {
        if (!open || centerPendingRef.current)
            return;
        const list = listRef.current;
        const row = activeRef.current;
        if (!list || !row)
            return;
        const top = row.offsetTop;
        const bottom = top + row.offsetHeight;
        if (top < list.scrollTop)
            list.scrollTop = top;
        else if (bottom > list.scrollTop + list.clientHeight) {
            list.scrollTop = bottom - list.clientHeight;
        }
    }, [open, activeIndex]);
    const openWith = useCallback((startIndex) => {
        setQuery("");
        setActiveIndex(startIndex);
        centerPendingRef.current = true;
        setOpen(true);
    }, []);
    const indexOfSelected = useCallback(() => {
        const index = options.findIndex((option) => option.value === value);
        return index >= 0 && !options[index]?.disabled
            ? index
            : firstEnabledIndex(options);
    }, [options, value]);
    const commit = useCallback((index) => {
        const option = filtered[index];
        if (!option || option.disabled)
            return;
        setOpen(false);
        // Guarded, because a controlled parent that re-renders on every emit would
        // otherwise churn when the user re-picks what is already selected — and
        // callers legitimately treat a change event as "the user changed it".
        if (option.value !== value)
            onValueChange(option.value);
    }, [filtered, onValueChange, value]);
    const handleTriggerKeyDown = (event) => {
        if (open)
            return;
        if (event.key === "ArrowDown" || event.key === "ArrowUp") {
            event.preventDefault();
            openWith(indexOfSelected());
            onOpen?.();
        }
    };
    const handleListKeyDown = (event) => {
        // The user has taken over; from here on the list follows the active row
        // by `nearest`, never re-centres on a late resize.
        centerPendingRef.current = false;
        switch (event.key) {
            case "ArrowDown":
                event.preventDefault();
                setActiveIndex((current) => nextEnabledIndex(filtered, current, 1));
                break;
            case "ArrowUp":
                event.preventDefault();
                setActiveIndex((current) => nextEnabledIndex(filtered, current, -1));
                break;
            case "Home":
                event.preventDefault();
                setActiveIndex(firstEnabledIndex(filtered));
                break;
            case "End":
                event.preventDefault();
                setActiveIndex(nextEnabledIndex(filtered, filtered.length, -1));
                break;
            case "Enter":
                event.preventDefault();
                commit(activeIndex);
                break;
            case "Tab":
                // Radix traps Tab inside the panel, which would strand the user on a
                // control with no visible focus. Closing hands focus back to the
                // trigger and lets the browser's own tab order take over.
                setOpen(false);
                break;
            default:
                break;
        }
    };
    return (_jsxs(Popover, { open: open, onOpenChange: (next) => {
            if (next) {
                openWith(indexOfSelected());
                onOpen?.();
                return;
            }
            setOpen(false);
        }, children: [_jsx(PopoverTrigger, { asChild: true, children: _jsxs("button", { ...triggerProps, ref: ref, type: "button", role: "combobox", "aria-label": ariaLabelledBy ? undefined : (ariaLabel ?? label), "aria-labelledby": ariaLabelledBy, "aria-expanded": open, "aria-haspopup": "listbox", "aria-controls": open ? listboxId : undefined, disabled: disabled, onKeyDown: handleTriggerKeyDown, className: cn(triggerVariant === "embedded"
                        ? mergedControlTriggerClasses
                        : controlClasses, fieldWidthClass(width), "items-center justify-between gap-2 text-start", className), children: [_jsx("span", { 
                            // Native title tooltip: the trigger truncates to one line, so a
                            // name wider than the box still has a way to be read in full on
                            // hover, with no extra popup component or dependency. It always
                            // carries the FULL label, which is what makes `triggerLabel` safe
                            // to be an abbreviation.
                            title: selected?.label, className: cn("line-clamp-1", selected === null && "text-placeholder"), children: selected?.triggerLabel ?? selected?.label ?? placeholder ?? "" }), _jsx(ChevronDown, { "aria-hidden": "true", className: "size-4 shrink-0 opacity-50" })] }) }), _jsxs(PopoverContent
            // Wheel / touch scrolling inside a Dialog — see `scrollableInDialog`.
            , { 
                // Wheel / touch scrolling inside a Dialog — see `scrollableInDialog`.
                ref: scrollableInDialog, 
                // Dismiss on a click outside and on Escape — but NOT merely because focus
                // left. Another overlay closing in the same dialog (a `Select` returning
                // focus to its own trigger a frame late) lands as a focus-outside on a
                // popup that opened a moment ago, and the list would vanish before it was
                // ever usable. Tab already closes deliberately (`handleListKeyDown`), so
                // nothing is lost by refusing this one.
                onFocusOutside: (event) => {
                    event.preventDefault();
                }, onOpenAutoFocus: (event) => {
                    // Radix would focus the first tabbable node. With a search field that
                    // is already right; without one there is nothing tabbable in the
                    // panel at all (options are `aria-activedescendant`-driven, not
                    // focusable), so focus would fall to the panel and the arrow keys
                    // would go nowhere. Place it explicitly in both cases.
                    event.preventDefault();
                    if (showSearch)
                        searchRef.current?.focus();
                    else
                        listRef.current?.focus();
                }, className: cn("w-[var(--radix-popover-trigger-width)] p-0", contentClassName), children: [!showSearch ? null : (_jsxs("div", { className: "flex items-center gap-2 border-b border-border px-3", children: [_jsx(Search, { "aria-hidden": "true", className: "size-4 shrink-0 text-muted" }), _jsx("input", { ref: searchRef, type: "text", autoComplete: "off", "aria-label": searchLabel, "aria-controls": listboxId, "aria-autocomplete": "list", "aria-activedescendant": activeIndex >= 0 ? optionId(activeIndex) : undefined, placeholder: searchPlaceholder, value: query, onChange: (event) => setQuery(event.target.value), onKeyDown: handleListKeyDown, className: cn("h-control w-full bg-transparent py-2 text-sm text-primary", "placeholder:text-placeholder focus-visible:outline-none") })] })), _jsx("div", { ref: attachList, id: listboxId, role: "listbox", "aria-label": label, 
                        // Focusable ONLY when there is no search field to hold focus for it.
                        tabIndex: showSearch ? -1 : 0, "aria-activedescendant": !showSearch && activeIndex >= 0 ? optionId(activeIndex) : undefined, onKeyDown: showSearch ? undefined : handleListKeyDown, className: cn("max-h-[min(18rem,var(--radix-popover-content-available-height))]", 
                        // `relative` makes the list the rows' `offsetParent`, so a row's
                        // `offsetTop` is measured from the top of the list — which is what
                        // `centerActiveRow` and the `nearest` effect both assume.
                        "relative overflow-y-auto overscroll-contain p-1 focus-visible:outline-none"), children: filtered.map((option, index) => {
                            const isActive = index === activeIndex;
                            const isSelected = option.value === value;
                            return (
                            // Both rules are wrong for this pattern, not merely inconvenient.
                            // In an `aria-activedescendant` listbox the options are
                            // deliberately NOT focusable and deliberately have no key
                            // handlers of their own — keyboard input belongs to the element
                            // that holds DOM focus (the search field, or the listbox), which
                            // is exactly what lets the user type and arrow at the same time.
                            // Making each option focusable would put it in the tab order and
                            // break that; duplicating the key handler on 400 rows would just
                            // be 400 chances to disagree with the one above.
                            // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/interactive-supports-focus -- keyboard lives on the aria-activedescendant owner
                            _jsxs("div", { ref: isActive ? activeRef : undefined, id: optionId(index), role: "option", "aria-selected": isSelected, "aria-disabled": option.disabled || undefined, onClick: () => commit(index), onPointerMove: () => {
                                    if (!option.disabled)
                                        setActiveIndex(index);
                                }, 
                                // Rows truncate to one line (`line-clamp-1` below), so the
                                // native title tooltip is what lets a long name — a nested
                                // location, say — be read in full without widening every row
                                // to its longest possible label.
                                title: option.label, className: cn("relative flex w-full cursor-default select-none items-center", "rounded-control py-2 pe-2 ps-8 text-sm", isActive && "bg-surface-hover", option.disabled && "pointer-events-none opacity-50"), children: [_jsx("span", { className: "absolute start-2 flex size-4 items-center justify-center", children: !isSelected ? null : (_jsx(Check, { "aria-hidden": "true", className: "size-4 text-brand" })) }), _jsx("span", { className: cn("line-clamp-1", indentFor(option.depth)), children: option.label })] }, option.value));
                        }) }), _jsx("p", { role: "status", className: cn(filtered.length > 0
                            ? "sr-only"
                            : "px-3 py-6 text-center text-sm text-muted"), children: filtered.length > 0 ? "" : emptyMessage })] })] }));
});
//# sourceMappingURL=searchable-select.js.map